import { useLocation, Location } from 'react-router-dom'
import { ArrayFieldValue, GroupedFieldValues } from '../../types'
import { useCallback, useRef, useState } from 'react'
import { YesObject } from '@yes.technology/react-toolkit'
import useNavigateMergingState from 'shared/hooks/useNavigateMergingState/useNavigateMergingState'

type SitemodelHistory = {
  url: string
  targetField?: string
  targetGroup?: string
  filterState?: GroupedFieldValues
  targetComponent?: 'FilterFields' | 'ObjectView'
}

type ValuesByTargetField = { [targetField: string]: YesObject[] }

export type PersistentSelection = {
  FilterFields?: GroupedFieldValues
  ObjectView?: ValuesByTargetField
}

export type LocationStateType = {
  sitemodelHistory?: SitemodelHistory[]
  persistentSelection?: PersistentSelection
  hasPersistentSelection?: boolean
  shouldPersistData?: boolean
  shouldResetFilters?: boolean
  shouldResetSelectedOptions?: boolean
  targetComponent?: 'FilterFields' | 'ObjectView'
  targetField?: string
  targetGroup?: string
  selectedOptions?: ArrayFieldValue
}

export const useLocationState = () => {
  const location: Location<LocationStateType | undefined> = useLocation()

  const locationState = location.state
  const persistentSelection = locationState?.persistentSelection
  const targetComponent = locationState?.targetComponent
  const targetField = locationState?.targetField
  const targetGroup = locationState?.targetGroup
  const selectedOptions = locationState?.selectedOptions

  const navigateMergingState = useNavigateMergingState<LocationStateType>()

  const hasPersistentSelection =
    persistentSelection &&
    Object.values(persistentSelection).some(
      (values) => Object.keys(values).length > 0
    )

  const shouldResetSelectedOptions = !!locationState?.shouldResetSelectedOptions
  const setShouldResetSelectedOptions = useCallback(
    (newShouldResetSelectedOptions: boolean) => {
      navigateMergingState(
        { search: location.search },
        {
          state: {
            shouldResetSelectedOptions: newShouldResetSelectedOptions
          },
          replace: true
        }
      )
    },
    [navigateMergingState, location.search]
  )

  const [shouldResetFilters, setShouldResetFilters] = useState(
    !!locationState?.shouldResetFilters
  )
  const [shouldPersistData, setShouldPersistData] = useState(
    !!locationState?.shouldPersistData
  )

  const discardPersistentSelection = useCallback(() => {
    if (!persistentSelection) return

    navigateMergingState(location.pathname + location.search, {
      replace: true,
      state: {
        persistentSelection: undefined,
        sitemodelHistory: [],
        targetComponent: undefined,
        targetField: undefined,
        targetGroup: undefined,
        selectedOptions: undefined
      }
    })
  }, [
    location.pathname,
    location.search,
    navigateMergingState,
    persistentSelection
  ])

  const latestLocationState = useRef<LocationStateType>()

  if (locationState !== latestLocationState.current) {
    if (locationState) {
      if (!!locationState.shouldResetFilters !== shouldResetFilters) {
        setShouldResetFilters(!!locationState.shouldResetFilters)
      }

      if (!!locationState.shouldPersistData !== shouldPersistData) {
        setShouldPersistData(!!locationState?.shouldPersistData)
      }
    }

    latestLocationState.current = locationState
  }

  return {
    locationState,
    persistentSelection,
    hasPersistentSelection,
    shouldResetFilters,
    shouldResetSelectedOptions,
    shouldPersistData,
    setShouldResetFilters,
    setShouldResetSelectedOptions,
    setShouldPersistData,
    targetComponent,
    targetField,
    targetGroup,
    selectedOptions,
    discardPersistentSelection
  }
}
